interface GAEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

// Cola de eventos en memoria
let eventQueue: any[] = [];
const QUEUE_FLUSH_INTERVAL = 60 * 1000; // 60 segundos
const QUEUE_MAX_SIZE = 15; // Límite de eventos acumulados para disparar envío inmediato (límite GA4 es 25)

// Obtener o generar un ID de cliente único persistente para trazar sesiones
const getAnalyticsClientId = (): string => {
  if (typeof window === 'undefined') return 'server';
  
  let clientId = localStorage.getItem('analytics_client_id');
  if (!clientId) {
    clientId = Math.random().toString(36).substring(2) + '.' + Math.floor(Date.now() / 1000);
    localStorage.setItem('analytics_client_id', clientId);
  }
  return clientId;
};

// Obtener el ID de usuario si está logueado en Firebase Auth
const getAnalyticsUserId = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return localStorage.getItem('analytics_user_id') || undefined;
};

// Obtener o generar un ID de sesión único para la pestaña/sesión actual (sessionStorage se limpia al cerrar pestaña)
const getAnalyticsSessionId = (): string => {
  if (typeof window === 'undefined') return '0';

  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = Math.floor(Date.now() / 1000).toString();
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Vaciar la cola de eventos y enviarla en lote (batch) al servidor
 */
export const flushQueue = (isUnloading = false) => {
  if (eventQueue.length === 0) return;

  const eventsToSend = [...eventQueue];
  eventQueue = [];

  const client_id = getAnalyticsClientId();
  const user_id = getAnalyticsUserId();

  const body = JSON.stringify({
    client_id,
    user_id,
    events: eventsToSend
  });

  if (isUnloading) {
    try {
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon('/api/track', new Blob([body], { type: 'application/json' }));
      } else {
        fetch('/api/track', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body,
          keepalive: true
        }).catch(() => {});
      }
    } catch (e) {
      // Fallback silencioso
    }
  } else {
    fetch('/api/track', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body
    }).catch(() => {
      // Silenciosamente ignorar fallos de red en analítica
    });
  }
};

/**
 * Inicializar Analíticas y Registrar Eventos de Ciclo de Vida
 */
export const initGA = () => {
  if (typeof window === 'undefined') return;

  if (import.meta.env.DEV) {
    console.log('🖥️ [Analytics] Inicializado en modo Servidor Proxy con Event Buffering (DEV logs activos)');
    return;
  }

  // Registrar envío periódico cada 60 segundos
  setInterval(() => {
    flushQueue(false);
  }, QUEUE_FLUSH_INTERVAL);

  // Registrar vaciado de cola al cerrar o recargar la pestaña
  window.addEventListener('beforeunload', () => {
    flushQueue(true);
  });
};

/**
 * Enviar evento genérico a la cola de analíticas
 */
export const trackEvent = ({ category, action, label, value }: GAEvent) => {
  if (import.meta.env.DEV) {
    console.log('📊 [Analytics][Queue-Event]', { category, action, label, value });
    return; // Evitar acumulación y peticiones locales en dev
  }

  const session_id = getAnalyticsSessionId();

  // Agregar evento a la cola
  eventQueue.push({
    name: action,
    params: {
      event_category: category,
      event_label: label,
      value: value,
      session_id: session_id,
      engagement_time_msec: '100'
    }
  });

  // Vaciar cola inmediatamente si alcanza el límite máximo
  if (eventQueue.length >= QUEUE_MAX_SIZE) {
    flushQueue(false);
  }
};

/**
 * Enviar vista de página (page_view) a la cola de analíticas
 */
export const trackPageView = (path: string, title?: string) => {
  if (import.meta.env.DEV) {
    console.log('🖥️ [Analytics][Queue-PageView]', { path, title: title || document.title });
    return; // Evitar acumulación y peticiones locales en dev
  }

  const session_id = getAnalyticsSessionId();
  const page_location = typeof window !== 'undefined' ? window.location.origin + path : path;

  // Agregar vista de página a la cola
  eventQueue.push({
    name: 'page_view',
    params: {
      page_location: page_location,
      page_title: title || document.title,
      session_id: session_id,
      engagement_time_msec: '100'
    }
  });

  // Vaciar cola inmediatamente si alcanza el límite máximo
  if (eventQueue.length >= QUEUE_MAX_SIZE) {
    flushQueue(false);
  }
};

// Eventos específicos de negocio

export const trackOnboardingStep = (step: number, stepName: string) => {
  trackEvent({
    category: 'Onboarding',
    action: 'step_completed',
    label: `step_${step}_${stepName}`,
    value: step
  });
};

export const trackOnboardingComplete = (duration: number) => {
  trackEvent({
    category: 'Onboarding',
    action: 'complete',
    label: 'onboarding_finished',
    value: Math.round(duration / 1000) // segundos
  });
};

export const trackSubscription = (
  plan: string,
  action: 'view' | 'start' | 'complete' | 'cancel'
) => {
  trackEvent({
    category: 'Subscription',
    action,
    label: plan
  });
};

export const trackActivityComplete = (type: 'activity' | 'checklist') => {
  trackEvent({
    category: 'Engagement',
    action: 'task_complete',
    label: type
  });
};

export const trackRewardRedeem = (rewardType: string, pointsCost: number) => {
  trackEvent({
    category: 'Rewards',
    action: 'redeem',
    label: rewardType,
    value: pointsCost
  });
};

export const trackTemplateImport = (templateId: string, templateType: string) => {
  trackEvent({
    category: 'Templates',
    action: 'import',
    label: `${templateType}_${templateId}`
  });
};

export const trackFamilyCreate = () => {
  trackEvent({
    category: 'Family',
    action: 'create',
    label: 'new_family'
  });
};

export const trackMemberAdd = (role: string) => {
  trackEvent({
    category: 'Family',
    action: 'member_add',
    label: role
  });
};

export const trackError = (errorMessage: string, fatal: boolean = false) => {
  if (import.meta.env.DEV) {
    console.warn('⚠️ [Analytics][Error]', { errorMessage, fatal });
    return;
  }

  const session_id = getAnalyticsSessionId();

  eventQueue.push({
    name: 'exception',
    params: {
      description: errorMessage,
      fatal,
      session_id: session_id,
      engagement_time_msec: '100'
    }
  });

  if (eventQueue.length >= QUEUE_MAX_SIZE) {
    flushQueue(false);
  }
};

export const trackSearch = (searchTerm: string, category?: string) => {
  if (import.meta.env.DEV) {
    console.log('🔍 [Analytics][Search]', { searchTerm, category });
    return;
  }

  const session_id = getAnalyticsSessionId();

  eventQueue.push({
    name: 'search',
    params: {
      search_term: searchTerm,
      category: category || 'general',
      session_id: session_id,
      engagement_time_msec: '100'
    }
  });

  if (eventQueue.length >= QUEUE_MAX_SIZE) {
    flushQueue(false);
  }
};

export const trackShare = (method: string, contentType: string) => {
  trackEvent({
    category: 'Social',
    action: 'share',
    label: `${contentType}_${method}`
  });
};

export const trackGoalAchieved = (goalType: string, streakDays?: number) => {
  trackEvent({
    category: 'Achievements',
    action: 'goal_achieved',
    label: goalType,
    value: streakDays
  });
};

/**
 * Configurar user properties (Se envía inmediatamente para actualizar metadatos del usuario)
 */
export const setUserProperties = (properties: {
  user_id?: string;
  plan_type?: string;
  family_size?: number;
  language?: string;
}) => {
  if (import.meta.env.DEV) {
    console.log('👤 [Analytics][UserProperties]', properties);
    return;
  }

  try {
    const client_id = getAnalyticsClientId();
    const session_id = getAnalyticsSessionId();
    const { user_id, ...restProps } = properties;

    fetch('/api/track', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        client_id,
        user_id: user_id || getAnalyticsUserId(),
        events: [
          {
            name: 'user_properties_update',
            params: {
              session_id: session_id,
              engagement_time_msec: '100'
            }
          }
        ],
        user_properties: restProps
      })
    }).catch(() => {});
  } catch {}
};
